import { useMemo } from "react";

import type { ISupportedLanguagesAndLocale } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import type { IRTCurrentCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import type { LocationDevice } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import type {
    FixedGscConfig,
    FixedGscMarketConfig,
} from "app/pages/digital-marketing/keywords-campaigns/shared/gsc-import/types";
import CountryService from "app/services/CountryService";

import { getDeviceFilterFn, getLocationOptionId } from "../utils";

const mapApiDeviceToLocationDevice = (device: string): LocationDevice => {
    if (device === DEVICES_TYPES_FOR_API.DESKTOP) {
        return "DESKTOP";
    }
    if (device === DEVICES_TYPES_FOR_API.MOBILE) {
        return "MOBILE";
    }
    return "BOTH";
};

export const useFixedGscConfig = (
    campaignConfig: IRTCurrentCampaignData,
    selectedDevice: LocationDevice,
    selectedLocationIds: Set<string>,
    languagesList: ISupportedLanguagesAndLocale[],
): FixedGscConfig | null => {
    return useMemo(() => {
        const scrapingConfigs = campaignConfig.scrapingConfigurations;
        const matchingConfigs = scrapingConfigs
            .filter(getDeviceFilterFn(selectedDevice))
            .filter((config) => selectedLocationIds.has(getLocationOptionId(config, selectedDevice)));

        if (matchingConfigs.length === 0) {
            return null;
        }

        const marketConfig: FixedGscMarketConfig[] = matchingConfigs
            .map((config) => {
                const country = CountryService.getCountryById(config.geoLocationCountry);
                const language = languagesList.find((lang) => lang.locale === config.locale);

                if (!country?.code || !language) {
                    return null;
                }

                return {
                    countryCode: country.code.toLowerCase(),
                    language: { id: language.locale, text: language.description },
                    device: mapApiDeviceToLocationDevice(config.device.toString()),
                };
            })
            .filter((config): config is FixedGscMarketConfig => config !== null);

        if (marketConfig.length === 0) {
            return null;
        }

        return {
            website: {
                site: campaignConfig.site,
                faviconUrl: campaignConfig.asset,
                domain: campaignConfig.site,
                displayText: campaignConfig.site,
            },
            marketConfig,
        };
    }, [campaignConfig, selectedDevice, selectedLocationIds, languagesList]);
};
